import {
  Button,
  Input,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useMyProjects } from '@/api/projects';
import { DateRangePicker } from '@/components/date-range-picker/DateRangePicker';
import type { WizardConditions } from './parts';

const RESOURCE_TYPE_ITEMS = [
  { value: 'both', label: 'Domains + Instances' },
  { value: 'domain', label: 'Domains' },
  { value: 'instance', label: 'Instances' },
];

/** Map the segmented value to the resource_types array. */
function segmentToTypes(value: string): ('domain' | 'instance')[] {
  if (value === 'domain') return ['domain'];
  if (value === 'instance') return ['instance'];
  return ['domain', 'instance'];
}

function typesToSegment(types: ('domain' | 'instance')[]): string {
  if (types.length === 1) return types[0];
  return 'both';
}

interface ConditionsStepProps {
  value: WizardConditions;
  onChange: (next: WizardConditions) => void;
  /** When set, the project is fixed and the project Select is hidden. */
  projectLocked: boolean;
}

export function ConditionsStep({ value, onChange, projectLocked }: ConditionsStepProps) {
  const { data: projects = [], isLoading: projectsLoading } = useMyProjects();

  const patch = (p: Partial<WizardConditions>) => onChange({ ...value, ...p });

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {!projectLocked ? (
        <Field
          label="Target project"
          required
          description="Resources matching your conditions will be gathered into this project."
        >
          {() => (
            <Select value={value.projectId} onValueChange={(v) => patch({ projectId: v })}>
              <SelectTrigger disabled={projectsLoading || projects.length === 0}>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      ) : null}

      <Field label="Resource type">
        {() => (
          <SegmentedControl
            items={RESOURCE_TYPE_ITEMS}
            value={typesToSegment(value.resourceTypes)}
            onValueChange={(v) => patch({ resourceTypes: segmentToTypes(v) })}
            aria-label="Resource type"
          />
        )}
      </Field>

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        <Field label="Status" description="Filter by resource status (optional).">
          {(p) => (
            <Input
              {...p}
              value={value.status}
              onChange={(e) => patch({ status: e.target.value })}
              placeholder="e.g. active, running"
            />
          )}
        </Field>

        <Field label="Search" description="Match resource name or id.">
          {(p) => (
            <Input
              {...p}
              value={value.search}
              onChange={(e) => patch({ search: e.target.value })}
              placeholder="Search name or id"
            />
          )}
        </Field>
      </div>

      <Field label="Tags" description="Comma or space separated. Matches resources having these tags.">
        {(p) => (
          <Input
            {...p}
            value={value.tagsInput}
            onChange={(e) => patch({ tagsInput: e.target.value })}
            placeholder="e.g. prod, edge"
          />
        )}
      </Field>

      {/* Same range control as the Business Usage export dialog (shared
          DateRangePicker). The filter is optional, so a Clear resets it. */}
      <Field label="Created between" description="Optional — only resources created in this period.">
        {() => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <DateRangePicker
                value={{ from: value.createdFrom, to: value.createdTo }}
                onChange={(r) => patch({ createdFrom: r.from, createdTo: r.to })}
                placeholder="Any time"
              />
            </div>
            {(value.createdFrom || value.createdTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => patch({ createdFrom: '', createdTo: '' })}
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </Field>

      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <Checkbox
          checked={value.unassignedOnly}
          onCheckedChange={(v) => patch({ unassignedOnly: v === true })}
          aria-label="Only show unassigned resources"
        />
        Only show unassigned resources
      </label>
    </div>
  );
}
